import { AuthContext } from "@vimana/ui-components/dist/Components/Auth/AuthContext";
import { Spinner } from "@vimana/ui-components/dist/Components/Core/Spinner";
import React, { ReactNode, useEffect, useState } from "react";
import { Session } from "vi-auth-session";

import { STORAGE_JWT_ISSUER } from "../constants";
import { autoLogin } from "../Dev/autoLogin";
import { isLocalEnvironment } from "../Dev/isDevelopment";
import { getHost } from "../Utils/fetchAPI";

// How far ahead of expiry to renew. The token lifetime itself is set server-side by
// VI_JWT_LIFETIME, so we read it off each token's `exp` rather than configuring it
// here — the two can then never disagree.
const REFRESH_LEAD_MS = 2 * 60 * 1000;

// `exp` is compared against the browser's clock. A display with a wrong clock — no NTP
// on a segmented plant network, a dead CMOS battery, an RTC holding local time — yields
// a nonsense lifetime, so anything outside this window is treated as untrustworthy and
// we fall back rather than schedule off it.
//   Lower bound must exceed REFRESH_LEAD_MS, or there is no room left to renew in.
//   Upper bound sits above the 604800s (7 day) ops-control default and far below what a
//   clock reset to a past year would produce.
const MIN_PLAUSIBLE_TTL_MS = 3 * 60 * 1000;
const MAX_PLAUSIBLE_TTL_MS = 8 * 24 * 60 * 60 * 1000;

// Used when the lifetime is untrustworthy, and as the initial guess before the first
// token arrives. Half the current staging lifetime (VI_JWT_LIFETIME=600), so a display
// keeps renewing rather than going dead. Revisit if that value changes materially.
const FALLBACK_REFRESH_MS = 5 * 60 * 1000;

// Bounded backoff, 45s in total. Sits inside REFRESH_LEAD_MS, so a retry that succeeds
// still lands before the current token expires.
const RETRY_BACKOFF_MS = [3 * 1000, 6 * 1000, 12 * 1000, 24 * 1000];

const getSession = (setLogoutFn, setToken, setLoading) => {
  let retryCount = 0;
  let retryTimer;
  let disposed = false;

  const session = new Session({
    issuer: STORAGE_JWT_ISSUER,
    logoutURL: `${getHost()}/api/v1/auth/logout`,
    refreshTokenURL: `${getHost()}/api/v1/auth/oauth/token`,
    refreshInterval: FALLBACK_REFRESH_MS,
    onStart: () => {},
    onLoginRequired: async function login() {
      if (isLocalEnvironment()) {
        await autoLogin(this.fetchTokenForDev.bind(this));
        window.location.reload();
      } else {
        document.location.assign(`${getHost()}/api/v1/auth/login`);
      }
    },
    onToken: function onToken(accessToken, refreshToken) {
      // A request still in flight when the component unmounted resolves here. dispose() left
      // _started false, so handleNewToken has just called handleStart(), which armed a fresh
      // refresh timer — dispose again to clear it, or it outlives the component.
      if (disposed) {
        this.dispose();
        return;
      }

      retryCount = 0;

      // handleNewToken decodes the token before calling us, so `exp` is already parsed.
      const lifetime = this._decodedToken.expiresAt.getTime() - Date.now();
      const trustworthy =
        Number.isFinite(lifetime) && lifetime > MIN_PLAUSIBLE_TTL_MS && lifetime < MAX_PLAUSIBLE_TTL_MS;

      // handleStart has already armed a timer using the previous interval; re-arm it
      // with the one this token actually calls for. scheduleNextTokenRefresh clears the
      // outstanding timer first, so this replaces rather than duplicates.
      this.refreshInterval = trustworthy ? lifetime - REFRESH_LEAD_MS : FALLBACK_REFRESH_MS;
      this.scheduleNextTokenRefresh();

      const logoutFn = function () {
        return function () {
          this.logout();
          setTimeout(() => window.location.reload(), 2000);
        }.bind(this);
      }.bind(this);
      setToken({ accessToken, refreshToken });
      setLogoutFn(logoutFn);
      setLoading(false);
    },
    onError: function onError(error) {
      // handleError disposed the session before calling us, so there is no timer left to
      // clear. Just stop: neither retrying nor logging out is right for a component that is
      // already gone.
      if (disposed) return;

      // A 4xx means the refresh token itself was rejected, and retrying cannot help.
      // Anything else — a network drop, a 5xx, svc-auth restarting — is worth retrying:
      // the refresh token is still good, and only the request failed.
      const status = error?.response?.status;
      const rejected = status >= 400 && status < 500;

      if (!rejected && retryCount < RETRY_BACKOFF_MS.length) {
        const delay = RETRY_BACKOFF_MS[retryCount];
        retryCount += 1;
        console.warn(`Token refresh failed, retrying in ${delay}ms`, error);
        retryTimer = setTimeout(() => {
          if (disposed) return;
          this.fetchNewToken().then(this.handleNewToken.bind(this)).catch(this.handleError.bind(this));
        }, delay);
        return;
      }

      console.error("Received authentication error", error);
      this.logout();
      setTimeout(() => window.location.reload(), 2000);
    },
    onLogout: () => {},
  });

  // The Session owns a setTimeout that outlives this component, and nothing else holds a
  // reference to it. Without this teardown, unmounting AuthController — ProtectedRoutes
  // returns null on /forgot-password — leaves a session refreshing on its own timer, and
  // remounting adds a second one rather than replacing the first. dispose() only stops the
  // timer: it does not clear localStorage or log out, so the remounted session picks the same
  // tokens straight back up.
  return () => {
    disposed = true;
    clearTimeout(retryTimer);
    session.dispose();
  };
};

type AuthControllerProps = {
  children: ReactNode;
};
export const AuthController = ({ children }: AuthControllerProps) => {
  const [loading, setLoading] = useState(true);
  const [logoutFn, setLogoutFn] = useState(null);
  const [token, setToken] = useState<any>({});
  useEffect(() => getSession(setLogoutFn, setToken, setLoading), []);
  if (loading) return <Spinner />;
  return (
    <AuthContext.Provider token={token.accessToken} refreshToken={token.refreshToken} logout={logoutFn}>
      {children}
    </AuthContext.Provider>
  );
};
